import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, User, Phone, MapPin } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  const [formData, setFormData] = useState({
    driverid: '',
    drivername: '',
    address1: '',
    address2: '',
    address3: '',
    phone: '',
    phone1: '',
    driverstatus: 'H',
    isactive: 1,
    isemployee: 1
  });

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/Drivers.php');
      
      if (response.data.success) {
        setDrivers(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load drivers');
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else {
        setError(err.response?.data?.message || 'Failed to load drivers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditData(null);
    setFormData({
      driverid: '',
      drivername: '',
      address1: '',
      address2: '',
      address3: '',
      phone: '',
      phone1: '',
      driverstatus: 'H',
      isactive: 1,
      isemployee: 1
    });
    setShowForm(true);
  };

  const handleEdit = (driver) => {
    setEditData(driver);
    setFormData({
      driverid: driver.driverid,
      drivername: driver.drivername || '',
      address1: driver.address1 || '',
      address2: driver.address2 || '',
      address3: driver.address3 || '',
      phone: driver.phone || '',
      phone1: driver.phone1 || '',
      driverstatus: driver.driverstatus || 'H',
      isactive: driver.isactive || 0,
      isemployee: driver.isemployee || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/Drivers.php`, {
        data: { driverid: id }
      });
      
      if (response.data.success) {
        alert('Driver deleted successfully!');
        loadDrivers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting driver: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.drivername.trim()) {
      alert('Driver name is required');
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        companyid: session?.companyid,
        isactive: formData.isactive ? 1 : 0,
        isemployee: formData.isemployee ? 1 : 0
      };

      const response = editData
        ? await apiClient.put('/Drivers.php', dataToSave)
        : await apiClient.post('/Drivers.php', dataToSave);
      
      if (response.data.success) {
        alert(editData ? 'Driver updated successfully!' : 'Driver created successfully!');
        setShowForm(false);
        setEditData(null);
        loadDrivers();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error saving driver: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.drivername?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm) ||
      driver.phone1?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && driver.isactive === 1) ||
      (statusFilter === 'inactive' && driver.isactive === 0);
    
    const matchesType = typeFilter === 'all' ||
      (typeFilter === 'hired' && driver.driverstatus === 'H') ||
      (typeFilter === 'contract' && driver.driverstatus === 'C');
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
  };

  if (!session?.companyid) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Drivers Master</h2>
          <p className="subtitle">Manage driver information and records</p>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Driver
        </button>
      </div>

      {/* Toolbar */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <select 
          className="filter-select"
          value={typeFilter} 
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="hired">Hired</option>
          <option value="contract">Contract</option>
        </select>

        <select 
          className="filter-select"
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
          <button onClick={clearFilters} className="btn-secondary">
            Clear Filters
          </button>
        )}

        <div className="toolbar-info">
          {filteredDrivers.length} {filteredDrivers.length === 1 ? 'driver' : 'drivers'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          Loading drivers...
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
          <button className="btn-primary" onClick={loadDrivers}>
            Retry
          </button>
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="empty-state">
          <User size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <p>No drivers found</p>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your filters' 
              : 'Get started by adding your first driver'}
          </p>
          {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
            <button className="btn-primary" onClick={handleNew}>
              <Plus size={20} />
              New Driver
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Driver Name</th>
                <th>Address</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Status</th>
                <th>Employee</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map(driver => (
                <tr key={driver.driverid}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User size={16} style={{ color: '#667eea' }} />
                      <span className="font-semibold">{driver.drivername}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      {driver.address1 && (
                        <div className="contact-item">
                          <MapPin size={14} />
                          <span>{driver.address1}</span>
                        </div>
                      )}
                      {driver.address2 && (
                        <div style={{ fontSize: '13px', color: '#6b7280', paddingLeft: '22px' }}>
                          {driver.address2}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      {driver.phone && (
                        <div className="contact-item">
                          <Phone size={14} />
                          <span>{driver.phone}</span>
                        </div>
                      )}
                      {driver.phone1 && (
                        <div className="contact-item">
                          <Phone size={14} />
                          <span>{driver.phone1}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`type-badge ${driver.driverstatus === 'H' ? 'corporate' : 'individual'}`}>
                      {driver.driverstatus === 'H' ? '🚗 Hired' : '📝 Contract'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${driver.isactive === 1 ? 'active' : 'inactive'}`}>
                      {driver.isactive === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`type-badge ${driver.isemployee === 1 ? 'corporate' : 'individual'}`}>
                      {driver.isemployee === 1 ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(driver)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(driver.driverid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editData ? 'Edit Driver' : 'New Driver'}</h3>
              <button className="btn-close" onClick={() => setShowForm(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-group">
                  <label>Driver Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="drivername"
                    value={formData.drivername}
                    onChange={handleChange}
                    required
                    placeholder="Enter driver name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="address1"
                      value={formData.address1}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="address2"
                      value={formData.address2}
                      onChange={handleChange}
                      placeholder="City, area"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address Line 3</label>
                  <input
                    type="text"
                    name="address3"
                    value={formData.address3}
                    onChange={handleChange}
                    placeholder="State, country"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Contact Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone 2</label>
                    <input
                      type="text"
                      name="phone1"
                      value={formData.phone1}
                      onChange={handleChange}
                      placeholder="Alternate phone"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Driver Type & Status</h4>
                
                <div className="form-group">
                  <label>Driver Type</label>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '12px' }}>
                    <label className="checkbox-label">
                      <input
                        type="radio"
                        name="driverstatus"
                        value="H"
                        checked={formData.driverstatus === 'H'}
                        onChange={handleChange}
                      />
                      <span>🚗 Hired</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="radio"
                        name="driverstatus"
                        value="C"
                        checked={formData.driverstatus === 'C'}
                        onChange={handleChange}
                      />
                      <span>📝 Contract</span>
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isactive"
                      checked={formData.isactive === 1}
                      onChange={handleChange}
                    />
                    <span>Active</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isemployee"
                      checked={formData.isemployee === 1}
                      onChange={handleChange}
                    />
                    <span>Is Employee</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editData ? 'Update Driver' : 'Create Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;